/**
 * Chat Store - Manages chat messages and streaming state
 * Uses Zustand for lightweight state management
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { v4 as uuidv4 } from 'uuid'
import type { ChatStoreState, Message } from '@/types/chat.types'

/**
 * Chat store implementation
 */
export const useChatStore = create<ChatStoreState>()(
  devtools(
    (set, get) => ({
      // State
      messages: [],
      sessionId: uuidv4(),
      streamingState: {
        isStreaming: false,
        currentMessageId: null,
        canAbort: false,
        abortController: null,
      },

      // Actions
      addMessage: (partialMessage: Omit<Message, 'id' | 'timestamp'>) => {
        const message: Message = {
          id: uuidv4(),
          timestamp: Date.now(),
          ...partialMessage,
        }
        set((state) => ({
          messages: [...state.messages, message],
        }))
        return message
      },

      updateMessage: (messageId: string, updates: Partial<Message>) => {
        set((state) => ({
          messages: state.messages.map((msg) =>
            msg.id === messageId ? { ...msg, ...updates } : msg
          ),
        }))
      },

      appendToMessageContent: (messageId: string, chunk: string) => {
        set((state) => ({
          messages: state.messages.map((msg) =>
            msg.id === messageId ? { ...msg, content: msg.content + chunk } : msg
          ),
        }))
      },

      updateMessageSources: (messageId: string, sources) => {
        set((state) => ({
          messages: state.messages.map((msg) => (msg.id === messageId ? { ...msg, sources } : msg)),
        }))
      },

      updateMessageAgentActivity: (messageId: string, agentActivity) => {
        set((state) => ({
          messages: state.messages.map((msg) =>
            msg.id === messageId ? { ...msg, agentActivity } : msg
          ),
        }))
      },

      updateMessageActions: (messageId: string, actions) => {
        set((state) => ({
          messages: state.messages.map((msg) => (msg.id === messageId ? { ...msg, actions } : msg)),
        }))
      },

      setStreamingStatus: (
        isStreaming: boolean,
        messageId: string | null,
        controller: AbortController | null
      ) => {
        set({
          streamingState: {
            isStreaming,
            currentMessageId: messageId,
            canAbort: isStreaming,
            abortController: controller,
          },
        })
      },

      clearChat: () => {
        // Abort ongoing streaming first
        const { abortController } = get().streamingState
        if (abortController) {
          abortController.abort()
        }

        set({
          messages: [],
          sessionId: uuidv4(),
          streamingState: {
            isStreaming: false,
            currentMessageId: null,
            canAbort: false,
            abortController: null,
          },
        })
      },

      abortStreaming: () => {
        const { abortController } = get().streamingState
        if (abortController) {
          abortController.abort()
          set((state) => ({
            streamingState: {
              ...state.streamingState,
              isStreaming: false,
              canAbort: false,
              abortController: null,
            },
          }))
        }
      },
    }),
    {
      name: 'chat-store',
    }
  )
)
